import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Download, BarChart3, TrendingUp, TrendingDown, 
  AlertTriangle, Package, DollarSign, Calendar, MapPin,
  Users, Target, CheckCircle, X, Clock, Filter,
  Search, RefreshCw, Eye, Edit, Trash2, MoreHorizontal,
  ChevronRight, ChevronLeft, Star, Heart, Zap, Activity
} from 'lucide-react';
import { 
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart as RechartsBarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart as RechartsPieChart, 
  Pie, Cell, ComposedChart
} from 'recharts';

const InventoryReports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedReport, setSelectedReport] = useState('stock-levels');
  const [dateRange, setDateRange] = useState('month');

  // Mock data for reports
  const reportData = {
    stockLevels: [
      { category: 'Beverages', current: 350, min: 200, max: 500, variance: 150 },
      { category: 'Dairy', current: 15, min: 30, max: 150, variance: -15 },
      { category: 'Bakery', current: 0, min: 50, max: 200, variance: -50 },
      { category: 'Produce', current: 75, min: 25, max: 100, variance: 50 },
      { category: 'Others', current: 120, min: 80, max: 200, variance: 40 }
    ],
    movementTrends: [
      { month: 'Jan', received: 800, transferred: 400, sold: 600, returned: 50 },
      { month: 'Feb', received: 900, transferred: 450, sold: 700, returned: 40 },
      { month: 'Mar', received: 700, transferred: 600, sold: 550, returned: 60 },
      { month: 'Apr', received: 1000, transferred: 500, sold: 800, returned: 30 },
      { month: 'May', received: 850, transferred: 550, sold: 750, returned: 45 },
      { month: 'Jun', received: 1100, transferred: 400, sold: 900, returned: 35 }
    ],
    valueAnalysis: [
      { category: 'Beverages', value: 8750, percentage: 35, trend: 'up' },
      { category: 'Dairy', value: 48, percentage: 25, trend: 'down' },
      { category: 'Bakery', value: 0, percentage: 20, trend: 'down' },
      { category: 'Produce', value: 667.5, percentage: 15, trend: 'up' },
      { category: 'Others', value: 600, percentage: 5, trend: 'stable' }
    ],
    accuracyMetrics: [
      { location: 'Warehouse A', accuracy: 98.5, counts: 15, discrepancies: 2 },
      { location: 'Cold Storage', accuracy: 95.2, counts: 8, discrepancies: 3 },
      { location: 'Bakery Section', accuracy: 100, counts: 12, discrepancies: 0 },
      { location: 'Produce Section', accuracy: 90.7, counts: 10, discrepancies: 4 }
    ]
  };

  const ReportCard = ({ title, value, change, icon: Icon, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">
            {title}
          </p>
          <p className="text-2xl font-bold mt-1 text-gray-900">
            {value}
          </p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${
              change > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {change > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const ChartCard = ({ title, children }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {title}
        </h3>
        <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
          <Download className="w-4 h-4" />
        </button>
      </div>
      {children}
    </motion.div>
  );

  const ReportTypeCard = ({ type, title, description, icon: Icon, isSelected, onClick }) => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`p-4 rounded-xl border transition-all text-left ${
        isSelected
          ? 'bg-blue-50 border-blue-200 text-blue-900'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
        isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-semibold text-gray-900">
        {title}
      </h3>
      <p className="text-sm mt-1 text-gray-600">
        {description}
      </p>
    </motion.button>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'stock-levels', label: 'Stock Levels', icon: Package },
    { id: 'movement', label: 'Movement', icon: TrendingUp },
    { id: 'value', label: 'Value Analysis', icon: DollarSign },
    { id: 'accuracy', label: 'Accuracy', icon: Target }
  ];

  const reportTypes = [
    {
      type: 'stock-levels',
      title: 'Stock Levels',
      description: 'Current inventory levels and stock status',
      icon: Package
    },
    {
      type: 'movement',
      title: 'Movement Trends',
      description: 'Inventory movement and transfer patterns',
      icon: TrendingUp
    },
    {
      type: 'value',
      title: 'Value Analysis',
      description: 'Inventory value and cost analysis',
      icon: DollarSign
    },
    {
      type: 'accuracy',
      title: 'Counting Accuracy',
      description: 'Physical count accuracy and discrepancies',
      icon: Target
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Inventory Reports</h2>
            <p className="text-gray-600">Comprehensive inventory analytics and reporting</p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            >
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">Last Year</option>
            </select>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Report
            </motion.button>
          </div>
        </div>

        {/* Apple-like Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReportCard
              title="Total Inventory Value"
              value="$125,000"
              change={12.5}
              icon={DollarSign}
              color="bg-green-500"
            />
            <ReportCard
              title="Total Items"
              value="1,250"
              change={8.3}
              icon={Package}
              color="bg-blue-500"
            />
            <ReportCard
              title="Low Stock Items"
              value="15"
              change={-5.2}
              icon={AlertTriangle}
              color="bg-yellow-500"
            />
            <ReportCard
              title="Average Accuracy"
              value="98.5%"
              change={2.1}
              icon={Target}
              color="bg-purple-500"
            />
          </div>

          {/* Report Types */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Available Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {reportTypes.map((report) => (
                <ReportTypeCard
                  key={report.type}
                  type={report.type}
                  title={report.title}
                  description={report.description}
                  icon={report.icon}
                  isSelected={selectedReport === report.type}
                  onClick={() => setSelectedReport(report.type)}
                />
              ))}
            </div>
          </div>

          {/* Stock Levels Chart */}
          <ChartCard title="Stock Levels by Category">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={reportData.stockLevels}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="category" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="current" fill="#3B82F6" name="Current Stock" />
                <Line type="monotone" dataKey="min" stroke="#EF4444" strokeWidth={2} name="Min Level" />
                <Line type="monotone" dataKey="max" stroke="#10B981" strokeWidth={2} name="Max Level" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Stock Levels Tab Content */}
      {activeTab === 'stock-levels' && (
        <div className="space-y-6">
          <ChartCard title="Stock Levels by Category">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={reportData.stockLevels}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="category" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="current" fill="#3B82F6" name="Current Stock" />
                <Line type="monotone" dataKey="min" stroke="#EF4444" strokeWidth={2} name="Min Level" />
                <Line type="monotone" dataKey="max" stroke="#10B981" strokeWidth={2} name="Max Level" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Stock Levels Table */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Stock Levels</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Category</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Current</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Min Level</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Max Level</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Variance</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.stockLevels.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-gray-900">{item.category}</td>
                      <td className="py-3 px-4 text-gray-900">{item.current}</td>
                      <td className="py-3 px-4 text-gray-900">{item.min}</td>
                      <td className="py-3 px-4 text-gray-900">{item.max}</td>
                      <td className={`py-3 px-4 ${item.variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.variance > 0 ? '+' : ''}{item.variance}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.current < item.min ? 'bg-red-100 text-red-800' :
                          item.current > item.max ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {item.current < item.min ? 'Low Stock' :
                           item.current > item.max ? 'Overstocked' :
                           'Normal'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Movement Tab Content */}
      {activeTab === 'movement' && (
        <div className="space-y-6">
          <ChartCard title="Inventory Movement Trends">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={reportData.movementTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="received" fill="#3B82F6" name="Received" />
                <Bar dataKey="transferred" fill="#10B981" name="Transferred" />
                <Bar dataKey="sold" fill="#F59E0B" name="Sold" />
                <Bar dataKey="returned" fill="#EF4444" name="Returned" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Value Analysis Tab Content */}
      {activeTab === 'value' && (
        <div className="space-y-6">
          <ChartCard title="Inventory Value by Category">
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={reportData.valueAnalysis}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {reportData.valueAnalysis.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#8B5CF6', '#3B82F6', '#F59E0B', '#10B981', '#EF4444'][index]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Accuracy Tab Content */}
      {activeTab === 'accuracy' && (
        <div className="space-y-6">
          <ChartCard title="Counting Accuracy by Location">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={reportData.accuracyMetrics}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="location" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="accuracy" fill="#10B981" name="Accuracy %" />
                <Line type="monotone" dataKey="counts" stroke="#3B82F6" strokeWidth={2} name="Counts" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Accuracy Table */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Accuracy Details</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Location</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Accuracy</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Counts</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Discrepancies</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.accuracyMetrics.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-gray-900">{item.location}</td>
                      <td className="py-3 px-4 text-gray-900">{item.accuracy}%</td>
                      <td className="py-3 px-4 text-gray-900">{item.counts}</td>
                      <td className="py-3 px-4 text-gray-900">{item.discrepancies}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.accuracy >= 98 ? 'bg-green-100 text-green-800' :
                          item.accuracy >= 95 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.accuracy >= 98 ? 'Excellent' :
                           item.accuracy >= 95 ? 'Good' :
                           'Needs Attention'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryReports; 